/* Branded invoice generated from a tracked job: agreed price, payments received, balance due.
   Uses the shared .print-only / .print-page styling (same as the quote invoice). */
const gbp = (n) => `£${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function JobInvoice({ user, logoUrl, job, invoiceNumber, paymentTerms }) {
  const today = new Date().toLocaleDateString('en-GB');
  const payments = job.payments || [];
  const received = job.received || 0;
  const balance = job.outstanding != null ? job.outstanding : (job.quoted_price || 0) - received;

  return (
    <div className="print-only" data-testid="job-invoice">
      <div className="print-page">
        <header className="flex items-start justify-between border-b border-slate-200 pb-4">
          <div className="flex items-start gap-4">
            {logoUrl ? <img src={logoUrl} alt="Logo" className="max-h-20 max-w-[160px] object-contain" /> : null}
            <div>
              <p className="font-display font-semibold text-2xl uppercase tracking-tight">{user?.business_name || user?.name || 'TradesMate User'}</p>
              {user?.business_address && <p className="whitespace-pre-line text-sm text-slate-700 mt-1">{user.business_address}</p>}
              <p className="text-sm text-slate-700 mt-1">
                {user?.business_phone && <span>{user.business_phone}</span>}
                {user?.business_phone && user?.business_email && <span> · </span>}
                {user?.business_email && <span>{user.business_email}</span>}
              </p>
              {user?.business_vat_number && <p className="text-xs text-slate-600 mt-1">VAT no: {user.business_vat_number}</p>}
            </div>
          </div>
          <div className="text-right">
            <p className="font-display font-semibold text-3xl uppercase tracking-tight">INVOICE</p>
            {invoiceNumber && <p className="font-mono text-sm mt-1">No. {invoiceNumber}</p>}
            <p className="font-mono text-xs text-slate-600 mt-1">Issued: {today}</p>
          </div>
        </header>

        <section className="grid grid-cols-2 gap-6 mt-6">
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Bill to</p>
            <p className="font-display font-bold text-lg mt-1">{job.customer_name || '—'}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Job</p>
            <p className="font-display font-bold text-lg mt-1">{job.name || 'Job'}</p>
          </div>
        </section>

        <section className="mt-8">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-y border-slate-200">
                <th className="text-left py-2 font-display uppercase text-xs tracking-wider">Description</th>
                <th className="text-right py-2 w-32 font-display uppercase text-xs tracking-wider">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-slate-300">
                <td className="py-3">Agreed price for works — {job.name || 'job'}{job.vat_rate > 0 ? ' (excl. VAT)' : ''}</td>
                <td className="py-3 text-right font-mono font-bold">{gbp(job.vat_rate > 0 ? job.net_price : job.quoted_price)}</td>
              </tr>
              {payments.length > 0 && (
                <tr><td colSpan={2} className="pt-4 pb-1 text-xs uppercase tracking-widest text-slate-500 font-semibold">Payments received</td></tr>
              )}
              {payments.map((p) => (
                <tr key={p.id} className="border-b border-slate-200">
                  <td className="py-2 text-slate-600">{p.date ? `${p.date} · ` : ''}{p.description || 'Payment'}</td>
                  <td className="py-2 text-right font-mono text-emerald-700">− {gbp(p.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className="flex justify-end mt-6">
          <table className="w-1/2 text-sm">
            <tbody>
              {job.vat_rate > 0 ? (
                <>
                  <tr><td className="py-1 text-slate-600">Subtotal (excl. VAT)</td><td className="text-right font-mono">{gbp(job.net_price)}</td></tr>
                  <tr><td className="py-1 text-slate-600">VAT ({job.vat_rate}%)</td><td className="text-right font-mono">{gbp(job.vat_amount)}</td></tr>
                  <tr className="border-t border-slate-300"><td className="py-1 font-bold">Total (incl. VAT)</td><td className="text-right font-mono font-bold">{gbp(job.quoted_price)}</td></tr>
                </>
              ) : (
                <tr><td className="py-1 text-slate-600">Agreed price</td><td className="text-right font-mono">{gbp(job.quoted_price)}</td></tr>
              )}
              <tr><td className="py-1 text-slate-600">Paid to date</td><td className="text-right font-mono text-emerald-700">− {gbp(received)}</td></tr>
              <tr className="border-t border-slate-200"><td className="py-2 font-display font-semibold text-lg uppercase">Balance due</td><td className="text-right font-mono font-semibold text-xl">{gbp(balance)}</td></tr>
            </tbody>
          </table>
        </section>

        <footer className="mt-10 pt-4 border-t border-slate-300 text-xs text-slate-500 font-mono">
          <p>{paymentTerms || 'Payment due within 14 days of invoice unless otherwise agreed.'}</p>
          <p className="mt-2">Generated with TradesMate UK · tradesmateuk.co.uk</p>
        </footer>
      </div>
    </div>
  );
}
